import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth';

type Step = 'email' | 'password' | 'check-email';

export default function Login() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function clear() {
    setError(null);
    setInfo(null);
  }

  async function handleCheckEmail(e?: React.FormEvent) {
    e?.preventDefault();
    if (!email.trim()) return;
    clear();
    setBusy(true);
    try {
      const res = await authApi.check(email.trim().toLowerCase());
      if (!res.exists) {
        setError('No account found for that email.');
      } else if (res.hasPassword) {
        setName(res.name ?? '');
        setStep('password');
      } else {
        await authApi.sendSetup(email.trim().toLowerCase());
        setInfo(`We sent a setup link to ${email}. Check your inbox to finish creating your account.`);
        setStep('check-email');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    clear();
    setBusy(true);
    try {
      const res = await authApi.login(email.trim().toLowerCase(), password);
      if ('needsSetup' in res) {
        setInfo('Your account needs setup. Check your email for a setup link.');
        await authApi.sendSetup(email.trim().toLowerCase());
        setStep('check-email');
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      navigate('/', { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleForgot(e: React.MouseEvent) {
    e.preventDefault();
    clear();
    setBusy(true);
    try {
      await authApi.forgot(email.trim().toLowerCase());
      setInfo(`If an account exists for ${email}, a password reset link has been sent.`);
      setStep('check-email');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function backToEmail() {
    setStep('email');
    setPassword('');
    setName('');
    clear();
  }

  return (
    <div className="login-shell">
      <div className="login-container">
        <div className="login-header">
          <div className="logo-mark">A</div>
          <div>
            <h1>Franchisee Marketing Dashboard</h1>
            <p>Amada Senior Care</p>
          </div>
        </div>

        {error && <div className="login-message error">{error}</div>}
        {info && <div className="login-message success">{info}</div>}

        {step === 'email' && (
          <form onSubmit={handleCheckEmail}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
            </div>
            <button type="submit" className="login-button" disabled={busy}>
              {busy ? 'Checking…' : 'Continue'}
            </button>
          </form>
        )}

        {step === 'password' && (
          <>
            {name && <div className="welcome-name">Welcome back, {name}</div>}
            <form onSubmit={handleLogin}>
              <input type="hidden" value={email} readOnly />
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <button type="submit" className="login-button" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign In'}
              </button>
            </form>
            <a href="#" className="forgot-link" onClick={handleForgot}>
              Forgot password?
            </a>
            <button type="button" className="change-email" onClick={backToEmail}>
              Use a different email
            </button>
          </>
        )}

        {step === 'check-email' && (
          <>
            <div className="check-email-box">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#2a5a8c" strokeWidth="1.5">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              <h3>Check your email</h3>
              <p>{info ?? 'A link has been sent.'}</p>
            </div>
            <button type="button" className="change-email" onClick={backToEmail}>
              Back to login
            </button>
          </>
        )}

        <div className="login-footer">Contact support for account access questions.</div>
      </div>
    </div>
  );
}
